import { createContext, useContext, useState, useRef, useEffect } from "react";

const VideoContext = createContext();

export const VideoProvider = ({ children }) => {
  const [activeVideo, setActiveVideo] = useState(null);
  const [muted, setMuted] = useState(true);
  const videosRef = useRef(new Set());
  const lastPlayedRef = useRef(null);

  const registerVideo = (video) => {
    if (!video) return;
    video.muted = muted; // sync to global mute state
    video._setIsPlaying ??= () => {}; // safety fallback
    videosRef.current.add(video);
  };

  const unregisterVideo = (video) => {
    if (!video) return;
    video.pause();
    video._setIsPlaying = undefined; // cleanup reference
    videosRef.current.delete(video);
  };

  const playVideo = async (video) => {
    if (!video) return;

    // Pause all others
    videosRef.current.forEach((v) => {
      if (v !== video) {
        v.pause();
        v._setIsPlaying?.(false);
      }
    });

    setActiveVideo(video);
    lastPlayedRef.current = video;

    try {
      await video.play();
      video._setIsPlaying?.(true);
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Video play failed:", err);
      }
    }
  };

  const toggleMute = () => {
    setMuted((prev) => !prev);
  };

  const pauseAll = () => {
    videosRef.current.forEach((v) => {
      v.pause();
      v._setIsPlaying?.(false);
    });
    setActiveVideo(null);
  };

  const resumeLast = () => {
    if (lastPlayedRef.current) {
      playVideo(lastPlayedRef.current);
    }
  };

  // 🔄 Keep all videos synced when mute changes
  useEffect(() => {
    videosRef.current.forEach((v) => (v.muted = muted));
  }, [muted]);

  return (
    <VideoContext.Provider
      value={{
        activeVideo,
        muted,
        playVideo,
        toggleMute,
        registerVideo,
        unregisterVideo,
        pauseAll,
        resumeLast,
      }}
    >
      {children}
    </VideoContext.Provider>
  );
};

export const useVideoContext = () => useContext(VideoContext);
